import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from 'axios'

// API base URL — runtime config takes precedence over build-time env.
//
// The `typeof window` guard is load-bearing, not defensive noise: this runs at
// module scope, so an unguarded `window` makes *every* module that transitively
// imports this one throw on import in any non-browser context. That included
// unit tests — this repo runs vitest without jsdom by choice, so importing the
// route store (→ routeStream → api) failed at collection time and the save path
// could not be tested at all until this guard existed. Behaviour in the browser
// is unchanged.
const API_BASE_URL =
  (typeof window !== 'undefined' ? window.__config__?.API_URL : undefined) ||
  import.meta.env.VITE_API_URL ||
  '/api/v1'

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // 2 minutes for route generation
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Send cookies for refresh token
})

// Request interceptor - add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('access_token')
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  }
)

// Response interceptor - handle errors and token refresh
let isRefreshing = false
let failedQueue: Array<{
  resolve: (value?: unknown) => void
  reject: (reason?: unknown) => void
}> = []

const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach(promise => {
    if (error) {
      promise.reject(error)
    } else {
      promise.resolve(token)
    }
  })
  failedQueue = []
}

api.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    // Handle 401 Unauthorized - try token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Queue the request while refreshing
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then(token => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`
            }
            return api(originalRequest)
          })
          .catch(err => Promise.reject(err))
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        // Try to refresh the token
        const response = await api.post('/auth/refresh')
        const { access_token } = response.data

        localStorage.setItem('access_token', access_token)

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${access_token}`
        }

        processQueue(null, access_token)
        return api(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError as AxiosError, null)

        // Clear auth and redirect to login
        localStorage.removeItem('access_token')

        // Only redirect if we're in a browser context
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/auth')) {
          const next = encodeURIComponent(window.location.pathname + window.location.search)
          window.location.href = `/auth/login?reason=session_expired&next=${next}`
        }

        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    // Handle other errors - emit event for toast notification
    const parsedError = parseApiError(error as AxiosError<ApiError>)
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('api-error', {
        detail: { message: parsedError.message }
      }))
    }

    return Promise.reject(error)
  }
)

// API error type
export interface ApiError {
  code: string
  message: string
  details?: Record<string, unknown>
}

// Parse API error from response
export function parseApiError(error: AxiosError<ApiError>): ApiError {
  if (error.response?.data) {
    return error.response.data
  }

  if (error.code === 'ECONNABORTED') {
    return {
      code: 'TIMEOUT',
      message: 'Request timed out. Please try again.',
    }
  }

  if (!error.response) {
    return {
      code: 'NETWORK_ERROR',
      message: 'Unable to connect to the server. Please check your connection.',
    }
  }

  return {
    code: 'UNKNOWN_ERROR',
    message: error.message || 'An unexpected error occurred.',
  }
}

// Typed request helpers
export async function get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const response = await api.get<T>(url, { params })
  return response.data
}

export async function post<T>(url: string, data?: unknown): Promise<T> {
  const response = await api.post<T>(url, data)
  return response.data
}

export async function put<T>(url: string, data?: unknown): Promise<T> {
  const response = await api.put<T>(url, data)
  return response.data
}

export async function patch<T>(url: string, data?: unknown): Promise<T> {
  const response = await api.patch<T>(url, data)
  return response.data
}

export async function del<T>(url: string, data?: unknown): Promise<T> {
  const response = await api.delete<T>(url, { data })
  return response.data
}

export default api
